/**
 * Zod schema for the batch-create form (PROV-BATCH).
 *
 * Owns SHAPE AND FIELD RULES only, mirroring the server's route-side
 * checks: email format + trim/lowercase normalization, non-empty name,
 * in-payload duplicates, 1..200 rows, a required cohort. Anything needing
 * server state — an address registered in another client, an ended
 * cohort — stays a server `detail` code rendered by the page.
 *
 * A mirror that drifts is worse than none: change this and
 * `apps/api-py/src/admin/enrollments/dto.py::CohortEnrollmentBatchBody`
 * together.
 */
import { z } from 'zod'
import { companyInformationShape } from './company-information-fields'
import {
  normalizeIdentity,
  enrollmentIdentityShape,
} from '../common/identity-schema.js'

/** Server cap (CohortEnrollmentBatchBody.rows max_length). Change together. */
export const MAX_BATCH_ROWS = 200

const batchRowSchema = z
  .object({
    sourceRow: z.number().optional(),
    parseError: z.string().optional(),
    parseErrorField: z.enum(['firstName', 'addressSuffix']).optional(),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .min(1, 'Email is required.')
      .email('Not a valid email address.'),
    ...enrollmentIdentityShape,
    ...companyInformationShape,
    placementRequirement: z
      .enum(['require_new_diagnostic', 'reuse_latest_result'])
      .default('require_new_diagnostic'),
  })
  .superRefine((row, ctx) => {
    if (row.parseError)
      ctx.addIssue({
        code: 'custom',
        path: [row.parseErrorField ?? 'lastName'],
        message: row.parseError,
      })
  })

export const createStudentBatchSchema = z
  .object({
    // The wave lands in exactly one tenant. No default and no fallback to
    // the operator's own client — unlike single create, a batch into
    // Cooori's tenant is the rare case, so an unchosen picker must stay
    // unchosen rather than silently target home.
    clientId: z.string().min(1, 'Choose a client.'),
    cohortId: z.string().min(1, 'Choose the cohort this wave belongs to.'),
    rows: z
      .array(batchRowSchema)
      .min(1, 'Add at least one student.')
      .max(
        MAX_BATCH_ROWS,
        `A batch is at most ${MAX_BATCH_ROWS} students — split a longer list.`,
      )
      // Duplicates are flagged on the LATER occurrence, matching the server:
      // the first row keeps the address, the repeat is the mistake.
      .superRefine((rows, ctx) => {
        const firstSeen = new Map<string, number>()
        rows.forEach((row, index) => {
          if (row.email === '') return
          const first = firstSeen.get(row.email)
          if (first === undefined) {
            firstSeen.set(row.email, index)
            return
          }
          ctx.addIssue({
            code: 'custom',
            path: [index, 'email'],
            message: `Duplicate of row ${rows[first]?.sourceRow ?? first + 1}.`,
          })
        })
      }),
  })
  .transform((value) => ({
    ...value,
    rows: value.rows.map((row) =>
      normalizeIdentity({
        ...(row.clientEmployeeId ? {clientEmployeeId: row.clientEmployeeId} : {}),
        ...(row.clientDepartment ? {clientDepartment: row.clientDepartment} : {}),
        email: row.email,
        firstName: row.firstName,
        lastName: row.lastName,
        username: row.username,
        addressSuffix: row.addressSuffix,
        placementRequirement: row.placementRequirement,
      }),
    ),
  }))

export type CreateStudentBatchFormInput = z.input<
  typeof createStudentBatchSchema
>
export type CreateStudentBatchForm = z.output<typeof createStudentBatchSchema>

/** Five identity columns, with optional trailing client employee ID and department. */
export interface ImportIdentityRow {
  clientEmployeeId?: string
  clientDepartment?: string
  parseError?: string
  parseErrorField?: 'firstName' | 'addressSuffix'
  email: string
  firstName: string
  lastName: string
  username: string
  addressSuffix: 'san' | 'none'
}
